/**
 * QUALITY-1 — runtime quality helpers (§12.0B).
 * Soft≠Live: offline freezes writes; dirty seats warn before leave;
 * soft error labels stay path-free. Response UI never imported here.
 */
import { useEffect, useState } from "react";
import { html, Alert } from "./ui.js";

let _dirty = false;
const _dirtyListeners = new Set();

export function setDirtySeat(on) {
  const next = !!on;
  if (next === _dirty) return;
  _dirty = next;
  _dirtyListeners.forEach((fn) => {
    try { fn(_dirty); } catch (_) { /* ignore */ }
  });
}

export function isDirtySeat() {
  return _dirty;
}

export function useDirtySeat() {
  const [dirty, setDirty] = useState(_dirty);
  useEffect(() => {
    const fn = (v) => setDirty(!!v);
    _dirtyListeners.add(fn);
    return () => { _dirtyListeners.delete(fn); };
  }, []);
  return dirty;
}

/** Soft operator copy for network / HTTP honesty (no paths). */
export function softQualityErrorOf(kind) {
  const k = String(kind || "").toLowerCase();
  if (k === "offline") return "You are offline. Changes stay paused until the connection returns.";
  if (k === "timeout") return "That request timed out. Try again in a moment.";
  if (k === "502" || k === "503") return "The service is momentarily unavailable. Reads may be stale; writes stay closed.";
  if (k === "429") return "Too many requests — wait a moment, then retry.";
  if (k === "409") return "Someone else changed this first. Re-read, then try again.";
  if (k === "403" || k === "401") return "Your seat cannot complete that write right now.";
  return "That action could not be completed.";
}

export function useOnline() {
  const [online, setOnline] = useState(() => {
    try {
      return typeof navigator === "undefined" ? true : navigator.onLine !== false;
    } catch {
      return true;
    }
  });
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

/** Shell banner + write freeze when offline (non-Response). */
export function QualityNetBanner({ primary }) {
  const online = useOnline();
  const dirty = useDirtySeat();
  useEffect(() => {
    try {
      document.documentElement.dataset.cmcNet = online ? "online" : "offline";
      document.documentElement.dataset.cmcDirty = dirty ? "1" : "0";
    } catch (_) { /* ignore */ }
  }, [online, dirty]);

  useEffect(() => {
    const onBefore = (e) => {
      if (!_dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, []);

  if (String(primary || "") === "response") return null;
  if (online && !dirty) return null;
  return html`<div className="cmc-quality-net" data-cmc-quality="net" role="status">
    ${!online ? html`<${Alert} tone="warn" title="Offline"
      say=${softQualityErrorOf("offline")}/>` : null}
    ${online && dirty ? html`<p className="note">Unsaved changes on this seat — leaving may discard the draft.</p>` : null}
  </div>`;
}

/** Writes must refuse while offline (UX gate; server still authoritative). */
export function writesAllowedOnline() {
  try {
    return typeof navigator === "undefined" ? true : navigator.onLine !== false;
  } catch {
    return true;
  }
}

/** CMC must not register a Service Worker (Q5). */
export function assertNoServiceWorkerRegistered() {
  try {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return true;
    const sw = navigator.serviceWorker;
    /* Never register; if something else registered, mark honesty for contracts. */
    if (typeof sw.getRegistrations === "function") {
      sw.getRegistrations().then((regs) => {
        try {
          document.documentElement.dataset.cmcSw = regs && regs.length ? "unexpected" : "none";
        } catch (_) { /* ignore */ }
      }).catch(() => {
        try { document.documentElement.dataset.cmcSw = "none"; } catch (_) { /* ignore */ }
      });
    } else {
      document.documentElement.dataset.cmcSw = "none";
    }
    return true;
  } catch {
    return true;
  }
}

export default {
  setDirtySeat,
  isDirtySeat,
  useDirtySeat,
  softQualityErrorOf,
  useOnline,
  QualityNetBanner,
  writesAllowedOnline,
  assertNoServiceWorkerRegistered,
};
